import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { JobTitleScreeningItem, JobTitleVerdict } from "@signalist/shared-types";

import { RootState } from "@/redux";

import { jobTitleScreeningApi } from "./jobTitleScreening.api";

interface JobTitleScreeningState {
	whitelisted: JobTitleScreeningItem[];
	excluded: JobTitleScreeningItem[];
	whitelistCount: number;
	excludedCount: number;
	search: string;
	/** Selected row ids for the multi-select move. */
	selectedIds: number[];
	/** Which column the current selection belongs to; the other column is locked. */
	selectionColumn: JobTitleVerdict | null;
}

const initialState: JobTitleScreeningState = {
	whitelisted: [],
	excluded: [],
	whitelistCount: 0,
	excludedCount: 0,
	search: "",
	selectedIds: [],
	selectionColumn: null,
};

export const jobTitleScreeningSlice = createSlice({
	name: "jobTitleScreening",
	initialState,
	reducers: {
		setSearch: (state, { payload }: PayloadAction<string>) => {
			state.search = payload;
		},
		toggleSelect: (state, { payload }: PayloadAction<{ id: number; column: JobTitleVerdict }>) => {
			// A selection is scoped to one column; ignore clicks in the locked column.
			if (state.selectionColumn && state.selectionColumn !== payload.column) {
				return;
			}

			const isSelected = state.selectedIds.includes(payload.id);

			state.selectedIds = isSelected
				? state.selectedIds.filter((id) => id !== payload.id)
				: [...state.selectedIds, payload.id];
			state.selectionColumn = state.selectedIds.length ? payload.column : null;
		},
		clearSelection: (state) => {
			state.selectedIds = [];
			state.selectionColumn = null;
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			jobTitleScreeningApi.endpoints.getJobTitles.matchFulfilled,
			(state, { payload }) => {
				state.whitelisted = payload.data.filter(
					(row) => row.verdict === JobTitleVerdict.WHITELISTED
				);
				state.excluded = payload.data.filter((row) => row.verdict === JobTitleVerdict.EXCLUDED);
				state.whitelistCount = Number(payload.whitelistCount ?? 0) || 0;
				state.excludedCount = Number(payload.excludedCount ?? 0) || 0;
				// Drop selections that no longer exist after a refetch (moved / deleted rows).
				const liveIds = new Set(payload.data.map((row) => row.id));

				state.selectedIds = state.selectedIds.filter((id) => liveIds.has(id));

				if (!state.selectedIds.length) {
					state.selectionColumn = null;
				}
			}
		);
	},
});

export const { setSearch, toggleSelect, clearSelection } = jobTitleScreeningSlice.actions;

export const jobTitleScreeningReducer = jobTitleScreeningSlice.reducer;

const selectSelf = (state: RootState) => state[jobTitleScreeningSlice.name];

const matchesSearch = (row: JobTitleScreeningItem, search: string): boolean =>
	row.title.toLowerCase().includes(search.trim().toLowerCase());

export const whitelistedTitlesSelector = (state: RootState): JobTitleScreeningItem[] => {
	const { whitelisted, search } = selectSelf(state);

	return search ? whitelisted.filter((row) => matchesSearch(row, search)) : whitelisted;
};

export const excludedTitlesSelector = (state: RootState): JobTitleScreeningItem[] => {
	const { excluded, search } = selectSelf(state);

	return search ? excluded.filter((row) => matchesSearch(row, search)) : excluded;
};

export const jobTitleCountsSelector = (
	state: RootState
): { whitelistCount: number; excludedCount: number } => {
	const { whitelistCount, excludedCount } = selectSelf(state);

	return { whitelistCount, excludedCount };
};

export const jobTitleSearchSelector = (state: RootState): string => selectSelf(state).search;

export const jobTitleSelectionSelector = (
	state: RootState
): { selectedIds: number[]; selectionColumn: JobTitleVerdict | null } => {
	const { selectedIds, selectionColumn } = selectSelf(state);

	return { selectedIds, selectionColumn };
};
